import { supabase } from "../supabaseClient";
import { torontoTodayKey } from "../components/torontoDate";
import { buildStandingsFromFixtures } from "./standingsFromFixtures";

/**
 * All registration teams this user is tied to (captain and/or roster player).
 * Supports multiple teams / divisions.
 */
export async function fetchAllLeagueTeamsForUser(user) {
  if (!user) return [];

  const teamMap = new Map();

  const { data: captainTeams } = await supabase
    .from("league_teams")
    .select("id, name, region, division, official_team_id, captain_id, registration_type, payment_status, season, logo_url, product_id, created_at")
    .eq("captain_id", user.id)
    .order("created_at", { ascending: false });

  (captainTeams || []).forEach((t) => {
    teamMap.set(t.id, { ...t, roles: new Set(["captain"]) });
  });

  const playerTeamIds = new Set();
  const { data: byUser } = await supabase
    .from("league_team_players")
    .select("team_id")
    .eq("user_id", user.id);
  (byUser || []).forEach((r) => r.team_id && playerTeamIds.add(r.team_id));

  if (user.email) {
    const { data: byEmail } = await supabase
      .from("league_team_players")
      .select("team_id")
      .ilike("email", user.email);
    (byEmail || []).forEach((r) => r.team_id && playerTeamIds.add(r.team_id));
  }

  const extraIds = [...playerTeamIds].filter((id) => !teamMap.has(id));
  if (extraIds.length) {
    const { data: ltRows } = await supabase
      .from("league_teams")
      .select("id, name, region, division, official_team_id, captain_id, registration_type, payment_status, season, logo_url, product_id, created_at")
      .in("id", extraIds);
    (ltRows || []).forEach((t) => {
      const existing = teamMap.get(t.id);
      if (existing) {
        existing.roles.add("player");
      } else {
        teamMap.set(t.id, { ...t, roles: new Set(["player"]) });
      }
    });
  }

  // Mark captains who also appear on roster
  teamMap.forEach((v, id) => {
    if (playerTeamIds.has(id)) v.roles.add("player");
  });

  return [...teamMap.values()]
    .filter((t) => t.payment_status !== "refunded")
    .map((t) => ({
      id: t.id,
      name: t.name,
      region: t.region,
      division: t.division,
      season: t.season,
      logo_url: t.logo_url,
      official_team_id: t.official_team_id,
      captain_id: t.captain_id,
      payment_status: t.payment_status,
      registration_type: t.registration_type,
      created_at: t.created_at,
      role: registrationRole(t, t.roles),
    }))
    .sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || String(b.created_at).localeCompare(String(a.created_at)));
}

const ROLE_ORDER = { captain: 0, player: 1, individual: 2 };

/**
 * How the signed-in user relates to a registration row:
 * "individual" signed up alone (the row is their own registration, `official_team_id` is
 * the team they were placed on), "captain" registered a team, "player" is on a roster.
 */
export function registrationRole(row, roles) {
  if (row?.registration_type === "individual") return "individual";
  return roles?.has("captain") ? "captain" : "player";
}

/** Resolve `teams.id` for standings / fixtures (merged id or name match fallback). */
export async function resolveOfficialTeamId(leagueTeam) {
  if (!leagueTeam) return null;
  if (leagueTeam.official_team_id) return leagueTeam.official_team_id;
  // An individual registration is named after the player, never a team.
  if (leagueTeam.registration_type === "individual") return null;

  let q = supabase
    .from("teams")
    .select("id")
    .eq("city", leagueTeam.region)
    .ilike("name", leagueTeam.name.trim());

  if (leagueTeam.region === "Cambridge" && leagueTeam.division) {
    q = q.ilike("division", leagueTeam.division);
  }

  const { data } = await q.maybeSingle();
  return data?.id ?? null;
}

/** Next upcoming or in-progress fixture for an official team in city/division. */
export async function fetchNextFixtureForOfficialTeam(city, division, officialTeamId) {
  if (!officialTeamId || !city) return null;

  let query = supabase
    .from("fixtures")
    .select(
      `
      id,
      date,
      time,
      field,
      home_team_id,
      away_team_id,
      home_team_score,
      away_team_score,
      game_played,
      home_team:home_team_id (name, logo_url),
      away_team:away_team_id (name, logo_url)
    `
    )
    .eq("city", city)
    .or(`home_team_id.eq.${officialTeamId},away_team_id.eq.${officialTeamId}`)
    .order("date", { ascending: true })
    .order("time", { ascending: true });

  if (city === "Cambridge") {
    if (!division) return null;
    query = query.ilike("division", division);
  }

  const { data, error } = await query;
  if (error || !data?.length) return null;

  const today = torontoTodayKey();
  const upcoming = data.filter((f) => f.date >= today);
  return upcoming[0] || data[data.length - 1];
}

const sameDivision = (a, b) => String(a || "").trim().toUpperCase() === String(b || "").trim().toUpperCase();

/** 1-based table position of `teamId` among `teams`, ranked from `fixtures` like the Standings page. */
export function placeInTable(teams, fixtures, teamId) {
  const table = buildStandingsFromFixtures(fixtures, teams);
  const idx = table.findIndex((r) => String(r.id) === String(teamId));
  if (idx === -1) return null;
  return { place: idx + 1, total: table.length, points: table[idx].points, teamName: table[idx].name };
}

/**
 * League table position (1-based) for a team in its own division and season, so teams from
 * other divisions or from earlier seasons never count.
 */
export async function computeStandingsPlace(city, division, officialTeamId) {
  if (!officialTeamId || !city) return null;

  const { data: own } = await supabase
    .from("teams")
    .select("division, season")
    .eq("id", officialTeamId)
    .maybeSingle();
  const teamDivision = own?.division || division;
  const season = own?.season || null;
  if (!teamDivision) return null;

  let teamsQuery = supabase.from("teams").select("id, name, division").eq("city", city);
  let fxQuery = supabase
    .from("fixtures")
    .select("home_team_id, away_team_id, home_team_score, away_team_score, is_forfeit")
    .eq("city", city)
    .ilike("division", teamDivision);
  if (season) {
    teamsQuery = teamsQuery.eq("season", season);
    fxQuery = fxQuery.eq("season", season);
  }

  const [{ data: teamsData, error: teamsError }, { data: fixturesData, error: fixturesError }] = await Promise.all([
    teamsQuery,
    fxQuery,
  ]);
  if (teamsError || fixturesError || !teamsData?.length) return null;

  const teams = teamsData.filter((t) => sameDivision(t.division, teamDivision));
  return placeInTable(teams, fixturesData || [], officialTeamId);
}

/** Roster line on a registration team for this user (for stats name match). */
export async function fetchMyRosterRowForTeam(user, leagueTeamId) {
  if (!user || !leagueTeamId) return null;

  let { data: row } = await supabase
    .from("league_team_players")
    .select("name, jersey_number, email")
    .eq("team_id", leagueTeamId)
    .eq("user_id", user.id)
    .maybeSingle();

  if (!row && user.email) {
    const res = await supabase
      .from("league_team_players")
      .select("name, jersey_number, email")
      .eq("team_id", leagueTeamId)
      .ilike("email", user.email)
      .maybeSingle();
    row = res.data;
  }

  return row;
}
